import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Spinner } from '@viquard/doc-ui';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  authKeys,
  resetAuthSessionCache,
  useMe,
  useResendVerification,
  verifyEmailRequest,
} from '@/features/auth/api';
import { AuthCard } from '@/features/auth/views';
import { ApiRequestError } from '@/lib/api';

type Status = 'idle' | 'pending' | 'success' | 'error';

const SearchSchema = z.object({
  token: z.string().min(1).optional(),
});

const STRONG_LINK_STYLE = {
  color: 'var(--vq-ink)',
  textDecoration: 'none',
  fontWeight: 500,
} as const;

type VerifyEmailResponse = Awaited<ReturnType<typeof verifyEmailRequest>>;

const verificationRequests = new Map<string, Promise<VerifyEmailResponse>>();

function verifyTokenOnce(token: string): Promise<VerifyEmailResponse> {
  const existing = verificationRequests.get(token);
  if (existing !== undefined) return existing;

  const request = verifyEmailRequest({ token }).finally(() => {
    globalThis.setTimeout(() => {
      verificationRequests.delete(token);
    }, 30_000);
  });
  verificationRequests.set(token, request);
  return request;
}

/** Lands the user from the verification email's `?token=...` link.
 *
 *  Shape mirrors `reset-password.tsx` — Zod-validated search param, AuthCard
 *  layout, branched on token presence. The token is consumed once: the API
 *  marks single-use rows on first hit, and the token-scoped promise cache
 *  defuses React StrictMode's dev remount without losing the eventual result.
 *  Resend is offered only when /me knows the email (signed-in viewer);
 *  anonymous arrivals fall back to a back-to-login footer. */
interface VerifyEmailContentProps {
  token: string | undefined;
  onContinue: () => void;
}

export function VerifyEmailContent({
  token,
  onContinue,
}: VerifyEmailContentProps): ReactNode {
  const { t } = useTranslation('auth');
  const qc = useQueryClient();
  const resend = useResendVerification();
  const me = useMe();
  const ownerEmail = me.data?.user.email ?? null;

  const [status, setStatus] = useState<Status>('idle');
  const [errCode, setErrCode] = useState<string | null>(null);
  const [resendOk, setResendOk] = useState(false);
  const [sessionEstablished, setSessionEstablished] = useState(false);

  useEffect(() => {
    if (token === undefined || token === '') return;
    let active = true;
    setStatus('pending');
    setErrCode(null);
    setSessionEstablished(false);
    void verifyTokenOnce(token)
      .then((response) => {
        if (!active) return;
        if (response.session !== null) {
          setSessionEstablished(true);
          void resetAuthSessionCache(qc, response.session);
        } else {
          void qc.invalidateQueries({ queryKey: authKeys.me });
        }
        setStatus('success');
      })
      .catch((err: unknown) => {
        if (!active) return;
        setErrCode(err instanceof ApiRequestError ? err.code : 'UNKNOWN');
        setStatus('error');
        void qc.invalidateQueries({ queryKey: authKeys.me });
      });
    return () => {
      active = false;
    };
  }, [qc, token]);

  // No token → user landed here from a bookmark or stale tab. Tell them how
  // to obtain a fresh link rather than showing a generic 404.
  if (token === undefined || token === '') {
    return (
      <AuthCard
        title={t('verifyEmail.missingTokenTitle')}
        subtitle={t('verifyEmail.missingTokenBody')}
        footer={
          <Link to="/login" style={STRONG_LINK_STYLE} className="hover:underline">
            {t('resetPassword.backToLogin')}
          </Link>
        }
      >
        <Link to="/" style={STRONG_LINK_STYLE} className="hover:underline">
          {t('verifyEmail.backHome')}
        </Link>
      </AuthCard>
    );
  }

  if (status === 'idle' || status === 'pending') {
    return (
      <AuthCard title={t('verifyEmail.pendingTitle')} subtitle={t('verifyEmail.pendingBody')}>
        <div className="flex justify-center py-2">
          <Spinner size="md" />
        </div>
      </AuthCard>
    );
  }

  if (status === 'success') {
    return (
      <AuthCard title={t('verify.successTitle')} subtitle={t('verify.successBody')}>
        {sessionEstablished ? (
          <Button
            variant="primary"
            size="md"
            shape="pill"
            onClick={onContinue}
          >
            {t('verify.continue')}
          </Button>
        ) : (
          <Link to="/login" style={STRONG_LINK_STYLE} className="hover:underline">
            {t('resetPassword.backToLogin')}
          </Link>
        )}
      </AuthCard>
    );
  }

  // status === 'error'
  // Map the most common verification-token failure codes to a precise body.
  // Anything else falls through to the generic failure copy.
  const subtitleKey =
    errCode === 'AUTH_EXPIRED_VERIFICATION_TOKEN'
      ? 'verifyEmail.expiredBody'
      : errCode === 'AUTH_INVALID_VERIFICATION_TOKEN'
        ? 'verifyEmail.invalidBody'
        : errCode === 'AUTH_ALREADY_VERIFIED'
          ? 'verifyEmail.alreadyVerifiedBody'
          : 'verifyEmail.failedBody';

  // `AUTH_ALREADY_VERIFIED` is the happy-failure path — the user clicked an
  // older link after already being verified. Send them straight to dashboard.
  if (errCode === 'AUTH_ALREADY_VERIFIED') {
    return (
      <AuthCard title={t('verify.successTitle')} subtitle={t(subtitleKey)}>
        <Button
          variant="primary"
          size="md"
          shape="pill"
          onClick={onContinue}
        >
          {t('verify.continue')}
        </Button>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t('verifyEmail.failedTitle')} subtitle={t(subtitleKey)}>
      <div className="flex flex-col gap-3">
        {ownerEmail !== null ? (
          <Button
            variant="primary"
            size="md"
            shape="pill"
            loading={resend.isPending}
            disabled={resendOk}
            onClick={() => {
              resend.mutate(
                { kind: 'email', email: ownerEmail },
                { onSuccess: () => { setResendOk(true); } },
              );
            }}
          >
            {resendOk ? t('verifyEmail.resendOk') : t('verifyEmail.resendCta')}
          </Button>
        ) : null}
        <div className="text-center">
          <Link to="/login" style={STRONG_LINK_STYLE} className="hover:underline">
            {t('resetPassword.backToLogin')}
          </Link>
        </div>
      </div>
    </AuthCard>
  );
}

function VerifyEmailRoute(): ReactNode {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  return (
    <VerifyEmailContent
      token={token}
      onContinue={() => {
        void navigate({ to: '/dashboard' });
      }}
    />
  );
}

export const Route = createFileRoute('/verify-email')({
  validateSearch: (search) => SearchSchema.parse(search),
  component: VerifyEmailRoute,
});
